/**
 * The text form of a query: the wildcard parser that turns what a person typed
 * into terms, the serializer that writes terms back as text, and `canonical`,
 * the semantic equality the two are tested against.
 *
 * Import it from `textQuery.ts`, which re-exports everything here.
 */

import {
  MAX_TERM_RUNES,
  MAX_TERMS,
  MIN_WILDCARD_RUNES,
  countCodePoints,
  fold,
  hasWhiteSpace,
  isWhiteSpace,
  trimWhiteSpace,
  validate,
  type ExecutableTerm,
  type Match,
  type Occur,
  type TextQueryTerm,
} from './textQuery';

// ── Types ────────────────────────────────────────────────────────────────

/**
 * The words that join the operands of an any-of group, each taken as a whole
 * word and exactly as spelled: `Oder` is a word like any other.
 */
export const DEFAULT_OR_KEYWORDS: readonly string[] = ['ODER', 'oder', 'OR', 'or'];

export interface ParseOptions {
  /**
   * The keywords of an any-of group, as whole words and as spelled. Replaces
   * {@link DEFAULT_OR_KEYWORDS}: a word that is not listed is a plain word.
   */
  orKeywords?: readonly string[];
  /**
   * Makes the id of each term the parse creates, once per term, in order.
   * Default: `tq-<n>` from a module counter. Never `crypto.randomUUID`, which
   * does not exist in an insecure context.
   */
  createId?: () => string;
  /**
   * The most terms the result may hold: the room a field has left when it
   * already holds terms. Default and upper bound {@link MAX_TERMS}.
   */
  maxTerms?: number;
}

/**
 * Why an item is not searched.
 *
 * - `questionMark` — an unescaped `?` in a word.
 * - `innerStar` — an unescaped `*` that is neither the first nor the last character of a word.
 * - `shortWildcard` — fewer than {@link MIN_WILDCARD_RUNES} literal characters beside a star.
 * - `emptyCase` — `=` (or `-=`) without a term behind it.
 * - `emptyTerm` — nothing to look for: a lone `-`, empty quotes, a word of combining marks only.
 * - `excludedInAnyOf` — an excluded operand (`-b`) in an any-of group (`a ODER -b`).
 * - `unclosedQuote` — a phrase that is never closed.
 * - `quoteNotSeparated` — text glued to the closing quote of a phrase (`"abc"def`, `"abc"*`,
 *   `"a""b"`): items are separated by white space.
 * - `tooManyTerms` — more terms than the query may hold.
 * - `termTooLong` — a term of more than {@link MAX_TERM_RUNES} code points.
 */
export type ParseIssueCode =
  | 'questionMark'
  | 'innerStar'
  | 'shortWildcard'
  | 'emptyCase'
  | 'emptyTerm'
  | 'excludedInAnyOf'
  | 'unclosedQuote'
  | 'quoteNotSeparated'
  | 'tooManyTerms'
  | 'termTooLong';

/** An item that was not turned into terms ("nicht gesucht"). */
export interface ParseIssue {
  code: ParseIssueCode;
  /** The raw text of the item (for a chain: of the operand or of the whole chain, see {@link parse}). */
  text: string;
  /** Where the raw text starts in the **input** (not in `rest`), in UTF-16 units. */
  start: number;
  /** Where it ends (exclusive), in UTF-16 units: `input.slice(start, end) === text`. */
  end: number;
}

/**
 * The other way to read a leading minus: as part of the text. `-0009` is
 * parsed as „not 0009“; the reading offers „stattdessen -0009 suchen“.
 */
export interface MinusReading {
  /** The `mustNot` term the minus made. */
  termId: string;
  /** The term to put in its place: same text with the minus, `must`, same match and case. */
  alternative: Omit<TextQueryTerm, 'id'>;
}

export interface ParseResult {
  /** The terms of the items that could be searched, in input order. */
  terms: TextQueryTerm[];
  /** One entry per item (or per bad operand of a chain) that could not. */
  issues: ParseIssue[];
  /** The raw text of every rejected item, in input order, joined by single spaces. */
  rest: string;
  /** One per `mustNot` that a leading minus made. */
  minusReadings: MinusReading[];
}

// ── Reading the input ────────────────────────────────────────────────────

/**
 * What a token is: a word, a closed phrase, a closed phrase with more text glued to
 * its closing quote, an unclosed one, or `-` / `=` with nothing behind.
 */
type Kind = 'word' | 'phrase' | 'glued' | 'unclosed' | 'prefix';

/** One atom of the input: a raw span of it. */
interface Token {
  kind: Kind;
  /**
   * The span of the raw text; for an unclosed phrase to the end of the input, trailing
   * white space trimmed; for a glued phrase to the end of everything glued to it.
   */
  start: number;
  end: number;
  /** Preceded by white space or the start of the input — a keyword has to be. */
  spaced: boolean;
  minus: boolean;
  caseSensitive: boolean;
  /** The span of the word, or of the phrase between its quotes. */
  bodyStart: number;
  bodyEnd: number;
}

/** The end of the phrase that opens at `open`: the first quote that is not escaped, or -1. */
function closingQuote(input: string, open: number): number {
  for (let at = open + 1; at < input.length; at += 1) {
    const char = input[at];
    if (char === '\\' && (input[at + 1] === '"' || input[at + 1] === '\\')) at += 1;
    else if (char === '"') return at;
  }
  return -1;
}

/**
 * Where the item ends that a closing quote right before `from` belongs to: at `from`
 * itself when white space or the end of the input follows, else behind everything
 * glued to the quote — further phrases back to back, and the characters up to the
 * next white space. A glued quote that never closes runs to the end of the input,
 * trailing white space trimmed, like any unclosed phrase.
 */
function gluedEnd(input: string, from: number): number {
  let end = from;
  while (input[end] === '"') {
    const close = closingQuote(input, end);
    if (close < 0) {
      end = input.length;
      while (end > from && isWhiteSpace(input.charCodeAt(end - 1))) end -= 1;
      return end;
    }
    end = close + 1;
  }
  while (end < input.length && !isWhiteSpace(input.charCodeAt(end))) end += 1;
  return end;
}

/**
 * Splits the input into atoms. White space separates them, except inside a
 * phrase. A phrase opens only at the start of the body of an atom — behind its
 * `-` and `=` — and closes at the first unescaped quote; white space or the end of
 * the input has to follow that quote, else the phrase and whatever is glued to it
 * are one atom of the kind `glued`.
 */
function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let at = 0;
  while (at < input.length) {
    if (isWhiteSpace(input.charCodeAt(at))) {
      at += 1;
      continue;
    }
    const start = at;
    const spaced = start === 0 || isWhiteSpace(input.charCodeAt(start - 1));
    const minus = input[at] === '-';
    if (minus) at += 1;
    const caseSensitive = input[at] === '=';
    if (caseSensitive) at += 1;
    const bodyStart = at;
    const base = { start, spaced, minus, caseSensitive };
    if (at >= input.length || isWhiteSpace(input.charCodeAt(at))) {
      tokens.push({ ...base, kind: 'prefix', end: at, bodyStart, bodyEnd: at });
    } else if (input[at] === '"') {
      const close = closingQuote(input, at);
      if (close < 0) {
        let end = input.length;
        while (end > start && isWhiteSpace(input.charCodeAt(end - 1))) end -= 1;
        tokens.push({ ...base, kind: 'unclosed', end, bodyStart: at + 1, bodyEnd: end });
        at = input.length;
      } else {
        const end = gluedEnd(input, close + 1);
        const kind = end > close + 1 ? 'glued' : 'phrase';
        tokens.push({ ...base, kind, end, bodyStart: at + 1, bodyEnd: close });
        at = end;
      }
    } else {
      while (at < input.length && !isWhiteSpace(input.charCodeAt(at))) at += 1;
      tokens.push({ ...base, kind: 'word', end: at, bodyStart, bodyEnd: at });
    }
  }
  return tokens;
}

/** The characters a backslash makes literal. Any other backslash stays what it is. */
const ESCAPABLE: ReadonlySet<string> = new Set(['\\', '"', '*', '?', '-', '=']);

/** One character of a body, and whether a backslash made it literal. */
interface Unit {
  char: string;
  escaped: boolean;
}

/** The characters (code points) of a body with the escapes resolved. */
function unitsOf(body: string): Unit[] {
  const chars = Array.from(body);
  const units: Unit[] = [];
  for (let at = 0; at < chars.length; at += 1) {
    const char = chars[at]!;
    const next = chars[at + 1];
    if (char === '\\' && next !== undefined && ESCAPABLE.has(next)) {
      units.push({ char: next, escaped: true });
      at += 1;
    } else {
      units.push({ char, escaped: false });
    }
  }
  return units;
}

/** What an atom comes to when it can be searched. */
interface Atom {
  text: string;
  match: Match;
  caseSensitive: boolean;
  /** A leading minus: excluded, unless it is a member of a group (then the atom is rejected). */
  minus: boolean;
}

type AtomResult = Atom | { code: ParseIssueCode };

/** The match mode and the text of a word, or the reason it is not searched. */
function wordOf(units: Unit[]): { match: Match; text: string } | { code: ParseIssueCode } {
  if (!units.length) return { code: 'emptyTerm' };
  if (units.some((unit) => unit.char === '?' && !unit.escaped)) return { code: 'questionMark' };
  const isStar = (at: number): boolean => units[at]!.char === '*' && !units[at]!.escaped;
  const last = units.length - 1;
  for (let at = 1; at < last; at += 1) if (isStar(at)) return { code: 'innerStar' };
  const leading = isStar(0);
  const trailing = last > 0 && isStar(last);
  const text = units
    .filter((_, at) => !((leading && at === 0) || (trailing && at === last)))
    .map((unit) => unit.char)
    .join('');
  if ((leading || trailing) && countCodePoints(text) < MIN_WILDCARD_RUNES) {
    return { code: 'shortWildcard' };
  }
  const match: Match =
    leading && trailing ? 'contains' : trailing ? 'startsWith' : leading ? 'endsWith' : 'contains';
  return { match, text };
}

function atomOf(input: string, token: Token): AtomResult {
  if (token.kind === 'unclosed') return { code: 'unclosedQuote' };
  if (token.kind === 'glued') return { code: 'quoteNotSeparated' };
  if (token.kind === 'prefix') return { code: token.caseSensitive ? 'emptyCase' : 'emptyTerm' };
  const units = unitsOf(input.slice(token.bodyStart, token.bodyEnd));
  let found: { match: Match; text: string } | { code: ParseIssueCode };
  if (token.kind === 'phrase') {
    found = { match: 'phrase', text: trimWhiteSpace(units.map((unit) => unit.char).join('')) };
  } else {
    found = wordOf(units);
  }
  if ('code' in found) return found;
  // What the wire would refuse: nothing to look for, or too much.
  if (!fold(found.text, { caseSensitive: token.caseSensitive }).text) return { code: 'emptyTerm' };
  if (countCodePoints(found.text) > MAX_TERM_RUNES) return { code: 'termTooLong' };
  return { ...found, caseSensitive: token.caseSensitive, minus: token.minus };
}

/** An item: one atom, or a chain `atom ODER atom …` (its tokens include the keywords). */
interface Item {
  tokens: Token[];
  operands: Token[];
}

/**
 * Groups the atoms into items. A keyword joins the atoms on both sides of it
 * only when it is a whole word with white space on both sides and both
 * neighbours are plain atoms: at the start, at the end, doubled, or next to a
 * quote it is a word like any other.
 */
function itemsOf(tokens: readonly Token[], isKeyword: readonly boolean[]): Item[] {
  const joins = (at: number): boolean =>
    at >= 1 &&
    at + 1 < tokens.length &&
    isKeyword[at]! &&
    !isKeyword[at - 1]! &&
    !isKeyword[at + 1]!;
  const items: Item[] = [];
  for (let at = 0; at < tokens.length; at += 1) {
    const item: Item = { tokens: [tokens[at]!], operands: [tokens[at]!] };
    while (joins(at + 1)) {
      item.tokens.push(tokens[at + 1]!, tokens[at + 2]!);
      item.operands.push(tokens[at + 2]!);
      at += 2;
    }
    items.push(item);
  }
  return items;
}

let counter = 0;
const nextId = (): string => `tq-${(counter += 1)}`;

/** The room a field has for new terms. */
function roomOf(max: number | undefined): number {
  if (max === undefined || !Number.isFinite(max)) return MAX_TERMS;
  return Math.max(0, Math.min(Math.floor(max), MAX_TERMS));
}

/**
 * Turns typed text into the terms of a query: wildcards are always on and the
 * text becomes chips at once. White space separates the items.
 *
 * ```
 * query  = { ws } [ item { ws+ item } ] { ws }
 * item   = atom { ws+ OR ws+ atom }             OR: ODER | oder | OR | or, a whole word
 * atom   = [ "-" ] [ "=" ] ( phrase | word )
 * phrase = '"' … '"'
 * word   = [ "*" ] text [ "*" ]
 * ```
 *
 * | Input | Becomes |
 * |---|---|
 * | `dicht` | `contains` „dicht“ (the default) |
 * | `dicht*` / `*dichtung` / `*dicht*` | `startsWith` / `endsWith` / `contains` |
 * | `"Dichtung TPE"` | `phrase` |
 * | `-PVC` | `mustNot` — a leading minus always means exclusion |
 * | `"-0009"` / `*-0009` / `\-0009` | a literal minus: `phrase` „-0009“ / `endsWith` „-0009“ / `contains` „-0009“ |
 * | `=AB` | `caseSensitive`; combines: `-=AB`, `=EPDM*`, `="Dichtung TPE"` |
 * | `EPDM ODER TPE` | both `should`: one any-of group |
 * | `4711\*` | a literal star |
 * | `%` `_` `+` `UND` `NICHT` | plain words |
 *
 * **Escapes.** A backslash makes the next character literal when that is one
 * of `\` `"` `*` `?` `-` `=`, in words and phrases alike: `\*` is a star,
 * `\"` a quote, `\\` a backslash, `\?` a question mark, and `\-` / `\=` a
 * minus / equals sign that would otherwise be a prefix. Any other backslash
 * stays what it is (`C:\temp`). White space cannot be escaped: use a phrase.
 *
 * **Where a character counts.** `-` and `=` are prefixes at the very start of
 * an item only, `-` before `=`; `=-a` is „-a“ with case, and `--a` excludes
 * „-a“. In a word a `*` is a wildcard only as its first or last character, a
 * `?` is never evaluated, and a `"` opens a phrase only at the start of the
 * body of an atom (behind the prefixes) — anywhere else it is a character, so
 * `5"` is a word. In a phrase everything is literal (`"4711*"`, `"why?"` — a
 * `?` inside quotes is no issue) except the escapes; its text is trimmed, the
 * white space inside stays as typed. A phrase closes at its first unescaped
 * quote, and white space (or the end of the input) has to follow it: text glued
 * to the closing quote (`"abc"def`, `"abc"*`, `"a""b"`) makes the phrase and all
 * that is glued to it one item, which is not evaluated.
 *
 * **Any-of.** Every atom of a chain becomes `should`, and all chains of one
 * input go into the one group of the model. A keyword is a keyword only as a
 * whole word with white space on both sides and a plain atom on each: at the
 * start or the end of the input, doubled (`a ODER ODER b`), or with a quote
 * glued to it (`ODER"b"` is one ordinary word) it is an ordinary word, and a
 * quoted `"oder"` always searches the word.
 *
 * **Not evaluated.** An item that cannot be searched becomes no term at all;
 * it gets an issue and its raw text is kept in `rest`, so the field can hold
 * on to it: a `?`, a star inside a word, fewer than 3 characters beside a
 * star, `=` (or `-=`) without a term, a lone `-`, empty quotes, `a ODER -b`
 * (an excluded operand is ambiguous), an unclosed quote (it runs to the end of
 * the input), text glued to the closing quote of a phrase (the item runs to
 * the next white space, or on through a glued phrase that follows), a term of
 * more than 80 code points, and more terms than the query may hold. Limits are
 * reported, never trimmed: the offending items stay in `rest`. Once an item
 * does not fit, the items behind it do not either. A chain is one item: if any
 * operand is bad, the whole chain is rejected — its issues name the bad
 * operands (`text`, `start` and `end` are the operand's), `excludedInAnyOf`
 * and `tooManyTerms` name the whole chain — and `rest` keeps it together, its
 * parts joined by single spaces.
 *
 * Every `mustNot` that a leading minus made adds a {@link MinusReading}; none
 * is offered when the literal reading (`-` and the text) would be longer than
 * {@link MAX_TERM_RUNES} code points: the wire refuses it, the exclusion stays.
 *
 * The terms pass {@link validate}: at most {@link MAX_TERMS} (or `maxTerms`),
 * none longer than {@link MAX_TERM_RUNES} code points, no white space in a term
 * that is not a phrase. A term object holds `caseSensitive` only when it is on.
 */
export function parse(input: string, options: ParseOptions = {}): ParseResult {
  const keywords = new Set(options.orKeywords ?? DEFAULT_OR_KEYWORDS);
  const createId = options.createId ?? nextId;
  const room = roomOf(options.maxTerms);
  const tokens = tokenize(input);
  const isKeyword = tokens.map(
    (token) =>
      token.kind === 'word' &&
      !token.minus &&
      !token.caseSensitive &&
      token.spaced &&
      keywords.has(input.slice(token.start, token.end))
  );

  const terms: TextQueryTerm[] = [];
  const issues: ParseIssue[] = [];
  const minusReadings: MinusReading[] = [];
  const rest: string[] = [];
  const issue = (code: ParseIssueCode, start: number, end: number): ParseIssue => ({
    code,
    text: input.slice(start, end),
    start,
    end,
  });

  let full = false;
  for (const item of itemsOf(tokens, isKeyword)) {
    const first = item.tokens[0]!;
    const last = item.tokens[item.tokens.length - 1]!;
    const atoms: Atom[] = [];
    const rejected: ParseIssue[] = [];
    for (const token of item.operands) {
      const atom = atomOf(input, token);
      if ('code' in atom) rejected.push(issue(atom.code, token.start, token.end));
      else atoms.push(atom);
    }
    const chain = item.operands.length > 1;
    if (!rejected.length && chain && atoms.some((atom) => atom.minus)) {
      rejected.push(issue('excludedInAnyOf', first.start, last.end));
    }
    if (!rejected.length && (full || terms.length + atoms.length > room)) {
      full = true;
      rejected.push(issue('tooManyTerms', first.start, last.end));
    }
    if (rejected.length) {
      issues.push(...rejected);
      rest.push(item.tokens.map((token) => input.slice(token.start, token.end)).join(' '));
      continue;
    }
    for (const atom of atoms) {
      const id = createId();
      const occur: Occur = chain ? 'should' : atom.minus ? 'mustNot' : 'must';
      terms.push({
        id,
        text: atom.text,
        occur,
        match: atom.match,
        ...(atom.caseSensitive ? { caseSensitive: true } : {}),
      });
      const literal = `-${atom.text}`;
      if (occur === 'mustNot' && countCodePoints(literal) <= MAX_TERM_RUNES) {
        minusReadings.push({
          termId: id,
          alternative: {
            text: literal,
            occur: 'must',
            match: atom.match,
            ...(atom.caseSensitive ? { caseSensitive: true } : {}),
          },
        });
      }
    }
  }
  return { terms, issues, rest: rest.join(' '), minusReadings };
}

// ── Writing the text form ────────────────────────────────────────────────

/**
 * Whether the text form can say a term, and `parse` read it back. It cannot say
 * a `startsWith` or `endsWith` term of fewer than {@link MIN_WILDCARD_RUNES}
 * code points (the text form refuses a short wildcard), nor one the wire
 * refuses (no text to look for, too long, white space in a term that is not a
 * phrase). Such a term stays in the JSON form of the query: it is neither
 * written by {@link serialize} nor ever parsed from text. `disabled` is not
 * looked at.
 */
export function isTextRepresentable(term: TextQueryTerm): boolean {
  if (validate({ v: 1, terms: [term] }) !== null) return false;
  if (term.match === 'startsWith' || term.match === 'endsWith') {
    return countCodePoints(trimWhiteSpace(term.text)) >= MIN_WILDCARD_RUNES;
  }
  return true;
}

/** A word's text with exactly the escapes `parse` needs; `leading`: no star stands before it. */
function escapeWord(text: string, leading: boolean): string {
  let out = '';
  let first = leading;
  for (const char of text) {
    if (char === '\\' || char === '*' || char === '?') out += `\\${char}`;
    else if (first && (char === '-' || char === '=' || char === '"')) out += `\\${char}`;
    else out += char;
    first = false;
  }
  return out;
}

/** A phrase: in quotes, with the backslashes and quotes inside it escaped. */
function quote(text: string): string {
  return `"${text.replace(/[\\"]/g, '\\$&')}"`;
}

function writeItem(term: TextQueryTerm, keywords: ReadonlySet<string>): string {
  const text = trimWhiteSpace(term.text);
  const prefix = `${term.occur === 'mustNot' ? '-' : ''}${term.caseSensitive === true ? '=' : ''}`;
  switch (term.match) {
    case 'phrase':
      return `${prefix}${quote(text)}`;
    case 'startsWith':
      return `${prefix}${escapeWord(text, true)}*`;
    case 'endsWith':
      return `${prefix}*${escapeWord(text, false)}`;
    default: {
      // A bare word equal to a keyword would join its neighbours; as a phrase of
      // one word it searches the same. What the reader compares is the written
      // word, escapes and all.
      const word = escapeWord(text, true);
      if (!prefix && keywords.has(word)) return quote(text);
      return `${prefix}${word}`;
    }
  }
}

export interface SerializeOptions {
  /** The keyword written between the members of the any-of group. Default `ODER`. */
  orKeyword?: string;
  /**
   * The keywords the text will be read with ({@link ParseOptions.orKeywords}): the
   * full active set, default {@link DEFAULT_OR_KEYWORDS}. A plain word equal to
   * one of them is written in quotes.
   */
  orKeywords?: readonly string[];
}

/**
 * Writes terms as the text {@link parse} reads back: the inverse for every
 * term that {@link isTextRepresentable}. Disabled terms and terms that are not
 * representable are left out (they stay in the JSON form).
 *
 * The round trip is **semantic**: `canonical(parse(serialize(t)).terms)`
 * equals `canonical(t)`, and `serialize(parse(text).terms)` writes the same
 * text again. The items are separated by one space, in order; the should terms
 * form one group, `a ODER b ODER c`, written where the first of them stands.
 * What the text needs escaped is escaped with a backslash — exactly the
 * following:
 *
 * - every backslash (`\\`);
 * - `*` and `?` in a word (`\*`, `\?`), not in a phrase;
 * - the first character of a word when it is `-`, `=` or `"` and no star
 *   stands before it (`\-0009`, but `*-0009`);
 * - in a phrase, `"` (`\"`).
 *
 * A plain word equal to a keyword is written in quotes: a phrase of one word
 * searches the same, and {@link canonical} treats it so. Which words count: the
 * ones of `options.orKeywords` (the set the text will be read with), the joiner
 * `options.orKeyword`, and always {@link DEFAULT_OR_KEYWORDS} — a text that is
 * read with another keyword set later keeps its meaning. A word is a keyword as
 * spelled: `Ou` is none for `OU`. The joiner has to be one of the keywords the
 * reader knows, or the group is read back as plain words.
 *
 * It does not enforce {@link MAX_TERMS}: a list of more usable terms is written
 * in full, and parsing that text reports `tooManyTerms` for the surplus.
 *
 * @param options.orKeyword The keyword written between the members of the any-of group; default `ODER`.
 * @param options.orKeywords The keywords the text will be read with; default {@link DEFAULT_OR_KEYWORDS}.
 */
export function serialize(terms: readonly TextQueryTerm[], options: SerializeOptions = {}): string {
  const keyword = options.orKeyword || DEFAULT_OR_KEYWORDS[0]!;
  const keywords = new Set([
    ...(options.orKeywords ?? DEFAULT_OR_KEYWORDS),
    keyword,
    ...DEFAULT_OR_KEYWORDS,
  ]);
  const usable = terms.filter((term) => !term.disabled && isTextRepresentable(term));
  const group = usable.filter((term) => term.occur === 'should');
  const items: string[] = [];
  let grouped = false;
  for (const term of usable) {
    if (term.occur !== 'should') {
      items.push(writeItem(term, keywords));
    } else if (!grouped) {
      grouped = true;
      items.push(group.map((member) => writeItem(member, keywords)).join(` ${keyword} `));
    }
  }
  return items.join(' ');
}

// ── Semantic equality ────────────────────────────────────────────────────

const OCCUR_ORDER: Record<Occur, number> = { must: 0, mustNot: 1, should: 2 };
const MATCH_ORDER: Record<Match, number> = { contains: 0, startsWith: 1, endsWith: 2, phrase: 3 };

/** Every run of white space as one space. */
function collapseWhiteSpace(text: string): string {
  let out = '';
  let inRun = false;
  for (let at = 0; at < text.length; at += 1) {
    if (isWhiteSpace(text.charCodeAt(at))) {
      if (!inRun) out += ' ';
      inRun = true;
    } else {
      out += text[at];
      inRun = false;
    }
  }
  return out;
}

/**
 * The query a list of terms says, in a form two lists can be compared in: what
 * the wire would carry, normalised and sorted. Two lists with the same
 * `canonical` search the same.
 *
 * - disabled terms are gone, ids are gone, `caseSensitive` is spelled out;
 * - the text is trimmed, and the white space in a phrase collapses to single
 *   spaces — a phrase matches that way;
 * - a phrase without white space is a `contains` term: it searches the same;
 * - one enabled `should` term is a `must` term: an any-of group of one;
 * - a `field`, when the term names one, is trimmed and kept; a blank field is
 *   every field and is left out. The text form cannot say a field, so this is
 *   what tells a sender term from a subject term;
 * - sorted by occur (`must`, `mustNot`, `should`), match (`contains`,
 *   `startsWith`, `endsWith`, `phrase`), text (by code unit), case (off
 *   first) and field (absent, then by code unit). Duplicates stay.
 */
export function canonical(terms: readonly TextQueryTerm[]): ExecutableTerm[] {
  const enabled = terms.filter((term) => !term.disabled);
  const shoulds = enabled.filter((term) => term.occur === 'should').length;
  return enabled
    .map((term): ExecutableTerm => {
      let text = trimWhiteSpace(term.text);
      let match = term.match;
      if (match === 'phrase') {
        text = collapseWhiteSpace(text);
        if (!hasWhiteSpace(text)) match = 'contains';
      }
      const executable: ExecutableTerm = {
        text,
        occur: term.occur === 'should' && shoulds === 1 ? 'must' : term.occur,
        match,
        caseSensitive: term.caseSensitive === true,
      };
      const field = typeof term.field === 'string' ? trimWhiteSpace(term.field) : '';
      if (field) executable.field = field;
      return executable;
    })
    .sort(
      (a, b) =>
        OCCUR_ORDER[a.occur] - OCCUR_ORDER[b.occur] ||
        MATCH_ORDER[a.match] - MATCH_ORDER[b.match] ||
        (a.text < b.text ? -1 : a.text > b.text ? 1 : 0) ||
        Number(a.caseSensitive) - Number(b.caseSensitive) ||
        ((a.field ?? '') < (b.field ?? '') ? -1 : (a.field ?? '') > (b.field ?? '') ? 1 : 0)
    );
}
